import React from 'react';
import { Form } from 'react-bootstrap';
import type { FormFieldProps } from '../../types/common';

export interface TextFieldProps extends FormFieldProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number';
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  size?: 'sm' | 'lg';
}

/**
 * Enhanced TextField component built on React Bootstrap
 * Provides consistent form field styling with validation support
 */
export const TextField: React.FC<TextFieldProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  type = 'text',
  maxLength,
  autoComplete,
  autoFocus = false,
  readOnly = false,
  size,
  helpText,
  validation,
  className = '',
  id,
  'data-testid': testId,
  ...props
}) => {
  const fieldId = id || `field-${name}`;
  const hasError = validation?.errors && validation.errors.length > 0;
  const hasWarning = validation?.warnings && validation.warnings.length > 0;

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <Form.Group className={`mb-3 ${className}`.trim()}>
      {label && (
        <Form.Label htmlFor={fieldId}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
      )}
      
      <Form.Control
        type={type}
        id={fieldId}
        name={name}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        size={size}
        isInvalid={hasError}
        isValid={validation?.isValid === true && !hasError && !hasWarning}
        data-testid={testId}
        aria-describedby={helpText ? `${fieldId}-help` : undefined}
        {...props}
      />

      {hasError && (
        <Form.Control.Feedback type="invalid">
          {validation?.errors?.join(', ')}
        </Form.Control.Feedback>
      )}

      {hasWarning && !hasError && (
        <div className="form-text text-warning">
          {validation?.warnings?.join(', ')}
        </div>
      )}

      {helpText && !hasError && !hasWarning && (
        <Form.Text id={`${fieldId}-help`} className="text-muted">
          {helpText}
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default TextField;
